// Anatomia do modal de fila — mockups "Modal Fila Dados / Chatbot /
// Horários" (900px): abas com ícone, corpo em duas colunas e o editor de
// opção do chatbot ao lado da lista.
import { makeStyles, alpha } from "@material-ui/core/styles";
import { AEROS, stateText } from "../../styles/aerosTokens";

const useQueueModalStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const borderSoft = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  const borderInput = isLight
    ? theme.palette.aeros.borderStrong
    : "rgba(255,255,255,0.1)";
  const fieldBg = isLight ? theme.palette.aeros.surface2 : "#12151F";
  const sideBg = isLight ? theme.palette.aeros.surface2 : "#0E1119";

  return {
    tabsRow: {
      display: "flex",
      gap: 4,
      padding: "0 24px",
      borderBottom: `1px solid ${borderSoft}`,
      overflowX: "auto",
    },
    tab: {
      padding: "10px 16px",
      fontSize: 13,
      fontWeight: 600,
      fontFamily: "inherit",
      background: "transparent",
      border: "none",
      borderBottom: "2px solid transparent",
      cursor: "pointer",
      color: theme.palette.text.secondary,
      display: "flex",
      gap: 6,
      alignItems: "center",
      whiteSpace: "nowrap",
      "& .MuiSvgIcon-root": { fontSize: 16 },
      "&:focus-visible": { outline: `2px solid ${AEROS.cyan}`, outlineOffset: -2 },
    },
    tabActive: {
      color: theme.palette.text.primary,
      borderBottomColor: theme.palette.primary.main,
    },

    body: {
      padding: "20px 24px",
      display: "flex",
      flexDirection: "column",
      gap: 16,
      minWidth: 0,
      overflowY: "auto",
      ...theme.scrollbarStylesSoft,
    },
    row3: {
      display: "grid",
      gridTemplateColumns: "minmax(0,1.6fr) minmax(0,1fr) 110px",
      gap: 12,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0,1fr)" },
    },
    pair: {
      display: "grid",
      gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
      gap: 12,
    },
    label: {
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      marginBottom: 6,
      display: "flex",
      alignItems: "center",
      gap: 4,
    },
    required: { color: AEROS.error },
    hint: { fontSize: 11, color: theme.palette.aeros.textTertiary, marginTop: 5 },
    input: {
      "& .MuiOutlinedInput-root": {
        backgroundColor: fieldBg,
        borderRadius: 10,
        fontSize: 13.5,
        color: theme.palette.text.primary,
        "& fieldset": { borderColor: borderInput },
        "&:hover fieldset": { borderColor: alpha(AEROS.cyan, 0.5) },
        "&.Mui-focused fieldset": { borderColor: AEROS.cyan, borderWidth: 1 },
        "&.Mui-focused": { boxShadow: `0 0 0 3px ${alpha(AEROS.cyan, 0.15)}` },
        "&.MuiOutlinedInput-multiline": { padding: 0 },
      },
      "& .MuiOutlinedInput-input": { padding: "9px 12px" },
      // Sem minHeight no multiline: o TextareaAutosize do MUI v4 mede por um
      // textarea sombra e um min-height ali estoura a altura do campo.
      "& .MuiOutlinedInput-inputMultiline": {
        padding: "10px 12px",
        fontSize: 13,
        lineHeight: 1.55,
      },
      "& .MuiSelect-select": { padding: "9px 12px" },
    },
    colorRow: { display: "flex", alignItems: "center", gap: 8 },
    swatchButton: {
      width: 38,
      height: 38,
      borderRadius: 10,
      flex: "none",
      border: `1px solid ${borderInput}`,
      cursor: "pointer",
      padding: 0,
    },

    optionRow: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      borderRadius: 12,
      padding: "10px 14px",
      "& > .MuiSvgIcon-root": {
        fontSize: 18,
        color: stateText(theme, "#C08BFF", "purple"),
        flex: "none",
      },
    },
    optionTitle: { fontSize: 13, fontWeight: 600, color: theme.palette.text.primary },
    optionDesc: { fontSize: 11.5, color: theme.palette.text.secondary, lineHeight: 1.35 },
    switchRoot: {
      width: 36,
      height: 20,
      padding: 0,
      display: "flex",
      overflow: "visible",
      flex: "none",
      "& .MuiSwitch-switchBase": {
        padding: 2,
        color: theme.palette.aeros.textTertiary,
        "&.Mui-checked": {
          transform: "translateX(16px)",
          color: "#07080D",
          "& + .MuiSwitch-track": {
            opacity: 1,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${AEROS.cyan})`,
          },
        },
        "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.4 },
      },
      "& .MuiSwitch-thumb": { width: 16, height: 16, boxShadow: "none" },
      "& .MuiSwitch-track": {
        borderRadius: 99,
        opacity: 1,
        backgroundColor: isLight
          ? theme.palette.aeros.borderStrong
          : "rgba(255,255,255,0.12)",
      },
    },

    // ---- aba Chatbot: lista à esquerda, editor à direita ----
    botSplit: {
      display: "grid",
      gridTemplateColumns: "minmax(0,1fr) minmax(0,1.3fr)",
      minHeight: 0,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "minmax(0,1fr)" },
    },
    botList: {
      padding: "20px 24px",
      display: "flex",
      flexDirection: "column",
      gap: 8,
      borderRight: `1px solid ${borderSoft}`,
      minWidth: 0,
      maxHeight: 460,
      overflowY: "auto",
      ...theme.scrollbarStylesSoft,
    },
    botEditor: {
      padding: "20px 24px",
      backgroundColor: sideBg,
      display: "flex",
      flexDirection: "column",
      gap: 14,
      minWidth: 0,
      maxHeight: 460,
      overflowY: "auto",
      ...theme.scrollbarStylesSoft,
    },
    eyebrow: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
    },
    optionCard: {
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      backgroundColor: fieldBg,
      border: `1px solid ${borderSoft}`,
      borderRadius: 12,
      padding: "10px 12px",
      cursor: "pointer",
      textAlign: "left",
      width: "100%",
      fontFamily: "inherit",
      minWidth: 0,
      "&:focus-visible": { outline: `2px solid ${AEROS.cyan}`, outlineOffset: 1 },
    },
    optionCardActive: {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      borderColor: alpha(theme.palette.primary.main, 0.45),
    },
    optionNumber: {
      width: 24,
      height: 24,
      borderRadius: 8,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 12,
      fontWeight: 700,
      flex: "none",
      backgroundColor: isLight ? theme.palette.divider : "rgba(255,255,255,0.08)",
      color: theme.palette.text.primary,
    },
    optionNumberActive: {
      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${AEROS.cyan})`,
      color: "#07080D",
    },
    optionName: {
      fontSize: 13,
      fontWeight: 600,
      color: theme.palette.text.primary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    optionMeta: {
      fontSize: 11,
      color: theme.palette.text.secondary,
      marginTop: 2,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    typePill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      fontSize: 10.5,
      fontWeight: 600,
      borderRadius: 6,
      padding: "1px 7px",
      marginTop: 4,
      "& .MuiSvgIcon-root": { fontSize: 12 },
    },
    addOption: {
      fontSize: 12.5,
      fontWeight: 600,
      textTransform: "none",
      color: stateText(theme, "#C08BFF", "purple"),
      border: `1px dashed ${alpha(theme.palette.primary.main, 0.4)}`,
      borderRadius: 12,
      padding: "10px 12px",
      justifyContent: "center",
      "& .MuiButton-startIcon .MuiSvgIcon-root": { fontSize: 16 },
    },
    typeGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(5,minmax(0,1fr))",
      gap: 6,
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "repeat(3,minmax(0,1fr))",
      },
    },
    typeCard: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 4,
      padding: "9px 4px",
      borderRadius: 10,
      cursor: "pointer",
      fontFamily: "inherit",
      fontSize: 11,
      fontWeight: 600,
      minWidth: 0,
      backgroundColor: fieldBg,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      color: theme.palette.text.secondary,
      "&:hover": { borderColor: alpha(theme.palette.primary.main, 0.4) },
      "&:focus-visible": { outline: `2px solid ${AEROS.cyan}`, outlineOffset: 1 },
      "& .MuiSvgIcon-root": { fontSize: 17 },
    },
    editorEmpty: {
      fontSize: 12.5,
      color: theme.palette.aeros.textTertiary,
      textAlign: "center",
      padding: "40px 0",
    },
    editorActions: {
      display: "flex",
      gap: 10,
      justifyContent: "flex-end",
      marginTop: "auto",
      paddingTop: 8,
    },

    schedulesArea: { padding: "20px 24px" },
    schedulesHint: {
      fontSize: 11.5,
      color: theme.palette.aeros.textTertiary,
      display: "flex",
      alignItems: "flex-start",
      gap: 6,
      marginTop: 12,
      lineHeight: 1.45,
      "& .MuiSvgIcon-root": { fontSize: 14, marginTop: 1, flex: "none" },
    },

    footer: {
      padding: "16px 24px",
      borderTop: `1px solid ${borderSoft}`,
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexWrap: "wrap",
    },
    footerActions: { marginLeft: "auto", display: "flex", gap: 10 },
    cancelButton: {
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.16)"}`,
      color: theme.palette.text.primary,
      borderRadius: 10,
      padding: "8px 16px",
      fontWeight: 600,
      fontSize: 13,
      textTransform: "none",
    },
    saveButton: {
      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${AEROS.cyan})`,
      color: "#07080D",
      borderRadius: 10,
      padding: "8px 18px",
      fontWeight: 700,
      fontSize: 13,
      textTransform: "none",
      boxShadow: "none",
      "&.Mui-disabled": { opacity: 0.5, color: "#07080D" },
    },
    ghostButton: {
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.14)"}`,
      color: theme.palette.text.secondary,
      borderRadius: 8,
      padding: "6px 12px",
      fontSize: 12,
      fontWeight: 600,
      textTransform: "none",
    },
    dangerIcon: {
      color: theme.palette.aeros.textTertiary,
      padding: 4,
      "&:hover": { color: AEROS.error, backgroundColor: "transparent" },
      "& .MuiSvgIcon-root": { fontSize: 16 },
    },
  };
});

export default useQueueModalStyles;
